import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Clock, Shield, LogOut, RefreshCw } from 'lucide-react';
import './TimeoutWarning.css';

const TimeoutWarning = () => {
  const { showTimeoutWarning, extendSession, logout, getTimeUntilLogout } = useAuth();
  const [countdown, setCountdown] = useState(0);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (showTimeoutWarning) {
      const interval = setInterval(() => {
        const timeLeft = getTimeUntilLogout();
        const seconds = Math.ceil(timeLeft / 1000);
        setCountdown(seconds);
        
        // Calculate progress percentage (5 minutes = 300 seconds)
        const totalSeconds = 300; // 5 minutes warning period
        const progressPercent = Math.max(0, (seconds / totalSeconds) * 100);
        setProgress(progressPercent);
        
        // If time runs out, the auth context will handle logout
        if (timeLeft <= 0) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [showTimeoutWarning, getTimeUntilLogout]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleExtendSession = () => {
    extendSession();
  };

  const handleLogoutNow = () => {
    logout();
  };

  if (!showTimeoutWarning) {
    return null;
  }

  // Calculate circumference for SVG circle (2 * π * radius)
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="timeout-warning-overlay">
      <div className="timeout-warning-modal">
        <div className="warning-header">
          <div className="warning-icon-wrapper">
            <AlertTriangle className="warning-icon" size={48} />
          </div>
          <h2 className="warning-title">Session Timeout Warning</h2>
        </div>
        
        <p className="warning-message">
          Your session will expire in <strong className="time-highlight">{formatTime(countdown)}</strong> due to inactivity.
        </p>
        
        <p className="warning-submessage">
          For your security, you will be automatically logged out unless you choose to continue.
        </p>

        <div className="countdown-display">
          <div className="countdown-container">
            <svg className="countdown-svg" viewBox="0 0 160 160">
              <defs>
                <linearGradient id="countdownGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ff6b35" />
                  <stop offset="50%" stopColor="#f44336" />
                  <stop offset="100%" stopColor="#d32f2f" />
                </linearGradient>
              </defs>
              {/* Background circle */}
              <circle
                className="countdown-circle-bg"
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                strokeWidth="8"
              />
              {/* Progress circle */}
              <circle
                className="countdown-circle-progress"
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                strokeWidth="8"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                transform="rotate(-90 80 80)"
              />
            </svg>
            <div className="countdown-content">
              <Clock className="countdown-clock-icon" size={24} />
              <span className="countdown-time">{formatTime(countdown)}</span>
              <span className="countdown-label">Time Remaining</span>
            </div>
          </div>
        </div>

        <div className="warning-actions">
          <button 
            onClick={handleExtendSession}
            className="extend-session-btn"
          >
            <RefreshCw size={18} />
            <span>Continue Session</span>
          </button>
          
          <button 
            onClick={handleLogoutNow}
            className="logout-now-btn"
          >
            <LogOut size={18} />
            <span>Logout Now</span>
          </button>
        </div>

        <div className="security-notice">
          <Shield size={16} />
          <small>
            This is a security feature to protect your account from unauthorized access.
          </small>
        </div>
      </div>
    </div>
  );
};

export default TimeoutWarning; 